import { useState } from 'react'
import FieldRow from './FieldRow'
import SectionCard from './SectionCard'
import { DateValueBox, DerivedValueBox, EditableValueBox } from './ValueBox'
import { formatScenarioDate, withPlaceholderPurchaseDate } from './scenarioDates'
import DisclosureToggle from './DisclosureToggle'
import SalesProceedsPanel from './SalesProceedsPanel'
import { formatMoney, formatTimelineMonths, isNetLoss } from './format'

// Only ARV has verbatim tooltip text in canvas-text.27.08.md ("Flip Summary" heading, Tool tips
// 2/2) -- every other row in this section (Purchase Price, Rehab Budget, the four derived rows,
// Purchase Date, Timeline) has no tooltip copy anywhere in the spec, so none is
// added; see README.md ("Flip Summary's card") for the styling gap this section
// otherwise carries.
// `**word**` = set in bold (700) in the canvas notes' running text (letter 99, 7.3.5; FlipInfoIcon.jsx).
const TOOLTIPS = {
  arv: '**After-Repair Value**: The property’s estimated market value after renovations.',
}

// Legacy stored the toggle's own displayed text as its value (`sale_date: 'Estimate'`,
// template.js:16, "a stored value, not just a label") -- same class of defect INTEREST_TYPE_OPTIONS
// exists to avoid (README.md, "the Interest dropdown"). Only one state has a real spec ('Estimate'
// displays as 'Projected'); no second label exists in any spec document read this session, so the
// control reuses the codebase's own established "unset toggle" sentinel (README.md, "the toggle
// sentinel": undefined/''/'%' are all legitimate unset states) rather than inventing a new one.
const SALE_DATE_ESTIMATE_VALUE = 'Estimate'
const SALE_DATE_ESTIMATE_LABEL = 'Projected'

function TimelineToggle({ active, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      tabIndex={-1}
      aria-checked={active}
      onClick={() => onChange(active ? '' : SALE_DATE_ESTIMATE_VALUE)}
      className={`text-[14px] font-bold outline-none focus-visible:ring-2 focus-visible:ring-brand-green rounded-[4px] ${
        active ? 'text-brand-green' : 'text-brand-dark'
      }`}
    >
      {SALE_DATE_ESTIMATE_LABEL}
    </button>
  )
}

// scenario/onFieldChange/onToggleChange/computed: see README.md ("no save endpoint yet", "the
// computed prop contract"). SectionCard `plain`: legacy-inventory.19.09.md's render map gives Flip
// Summary no collapse key, and the design draws no badge or total -- README.md ("Flip Summary's card").
// purchaseDatePlaceholder: ISO today + 14 (letter 104, response 2), shown in grey while Purchase Date is
// blank; the sale date beside Timeline follows it, as the compute does. Never handed up as a value.
export default function FlipSummarySection({ scenario, computed, onFieldChange, onToggleChange, purchaseDatePlaceholder = null }) {
  const s = scenario ?? {}
  const saleDate = withPlaceholderPurchaseDate(s, purchaseDatePlaceholder).sales_date
  const toggles = s.toggles ?? {}
  // D12: local component state, default collapsed, not persisted -- Flip Summary itself has no
  // collapse key (README.md, "Flip Summary's card"), and the design shows no
  // persisted state for this subsection either.
  const [salesProceedsExpanded, setSalesProceedsExpanded] = useState(false)
  const netLoss = isNetLoss(computed?.netProfit)

  return (
    // `plain`: no badge, total or collapse button, as drawn. 16 below the header, as every v3 card
    // draws it (first box at card top + 64).
    <SectionCard title="Flip Summary" firstRowGap={16} plain>
      <div className="flex flex-col">
        <FieldRow label="ARV" tooltip={TOOLTIPS.arv}>
          <EditableValueBox money value={String(s.arv ?? '')} onChange={(v) => onFieldChange('arv', v)} ariaLabel="ARV" />
        </FieldRow>

        <div className="mt-[8px]">
          <FieldRow label="Purchase Price">
            <EditableValueBox money
              value={String(s.purchase_price ?? '')}
              onChange={(v) => onFieldChange('purchase_price', v)}
              ariaLabel="Purchase Price"
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Rehab Budget">
            <EditableValueBox money
              value={String(s.rehab_costs ?? '')}
              onChange={(v) => onFieldChange('rehab_costs', v)}
              ariaLabel="Rehab Budget"
            />
          </FieldRow>
        </div>

        {/* Overbudget row removed (canvas-text.27.08.md). The field itself is not: calcTotalExpenses
            (legacy calcAllIn), calcNetProfit and all four calcScore weights still take it as a
            parameter -- see defaults.js's own comment and README.md. */}

        <div className="mt-[8px]">
          {/* "Closing Costs", not "Total ...": Cong, letter 111, Comment Set #3 item 2 (this row only). */}
          <FieldRow label="Closing Costs">
            <DerivedValueBox value={formatMoney(computed?.closing_costs)} ariaLabel="Closing Costs (Flip Summary)" />
          </FieldRow>
        </div>

        {/* "(Flip Summary)" avoids the same collision "Selling Costs" hits:
            HoldingCostsSection draws its own "Holding Costs" heading on the same page. */}
        <div className="mt-[8px]">
          <FieldRow label="Holding Costs">
            <DerivedValueBox value={formatMoney(computed?.holding_cost)} ariaLabel="Holding Costs (Flip Summary)" />
          </FieldRow>
        </div>

        {/* Reads selling_costs, not at_closing -- the Selling Costs section's own body total reads
            at_closing; this rollup row and that section's own header total both read selling_costs
            (at_closing + marketing2), matching the design ($26,000, Marketing included) and the
            workbook (Loan 2026 Q3 Q14 =L36, "Selling Costs" = MROUND(L37+L42,5), L37 Marketing).
            See README.md ("at_closing vs selling_costs"). */}
        <div className="mt-[8px]">
          <FieldRow label="Selling Costs">
            <DerivedValueBox value={formatMoney(computed?.selling_costs)} ariaLabel="Selling Costs (Flip Summary)" />
          </FieldRow>
        </div>

        {/* Total-row treatment (spec 6): bold label, bold value, the value-width top divider --
            the same DerivedValueBox `bold`/`topDivider` pair Acquisition Costs and Selling Costs'
            own totals already use (ValueBox.jsx:23-43, SellingCostsSection.jsx:144). No icon on
            either row (canvas-text.27.08.md: "Remove tool tips for 'Total Expenses' and 'Net
            Profit' (on Flip Summary only)"), and the 40 pitch is unchanged -- no extra gap above. */}
        <div className="mt-[8px]">
          <FieldRow label="Total Expenses" weight="bold">
            <DerivedValueBox value={formatMoney(computed?.totalExpenses)} bold topDivider ariaLabel="Total Expenses" />
          </FieldRow>
        </div>

        {/* Net Profit: label in the ordinary dark, value #1F7A2E green (spec 6). A net figure that is
            strictly negative after rounding reads "Net Loss", label and value both #D85263
            (brand-statusSale) -- Figma "General Notes for Dev Team": "If Net Loss appears, use
            D85263 red color for the text and value." Same isNetLoss rule as the Metrics strip
            (format.js); the value keeps its minus sign and full figure (-$26,500). README.md ("Net
            Loss is strictly negative"). */}
        <div className="mt-[8px]">
          <FieldRow label={netLoss ? 'Net Loss' : 'Net Profit'} weight="bold" color={netLoss ? 'text-brand-statusSale' : 'text-brand-dark'}>
            <DerivedValueBox
              value={formatMoney(computed?.netProfit)}
              bold
              valueColorClass={netLoss ? 'text-brand-statusSale' : 'text-[#1F7A2E]'}
              ariaLabel={netLoss ? 'Net Loss' : 'Net Profit'}
            />
          </FieldRow>
        </div>

        {/* 32 gap before Purchase Date (spec 1: 1429 -> 1461), not the standard 8 -- no divider
            line is drawn there either. */}
        <div className="mt-[32px]">
          <FieldRow label="Purchase Date">
            <DateValueBox
              value={s.purchase_date}
              onChange={(v) => onFieldChange('purchase_date', v)}
              placeholder={purchaseDatePlaceholder ? formatScenarioDate(purchaseDatePlaceholder) : undefined}
              ariaLabel="Purchase Date"
            />
          </FieldRow>
        </div>

        {/* Timeline (flip-financing-v3 / flip-cash-v3): months typed into the bordered 68 box
            (holding_period), then the sale date, calculated from them and not editable, in the
            borderless 88 box; 68 + 8 + 1 px line + 7 + 88 = 172, as drawn (3426 -> 3598). The sale
            date itself is set by the page (scenarioDates.js withDerivedSaleDate). */}
        <div className="mt-[8px]">
          <FieldRow
            label="Timeline"
            control={
              <TimelineToggle
                active={toggles.sale_date === SALE_DATE_ESTIMATE_VALUE}
                onChange={(v) => onToggleChange('sale_date', v)}
              />
            }
          >
            <EditableValueBox
              value={formatTimelineMonths(s.holding_period)}
              onChange={(v) => onFieldChange('holding_period', v)}
              width={68}
              ariaLabel="Timeline (months)"
            />
            <span className="ml-[2px] mr-[1px] w-px h-[16px] bg-brand-fieldBdr shrink-0" aria-hidden="true" />
            <DerivedValueBox value={formatScenarioDate(saleDate) || null} ariaLabel="Sale Date" />
          </FieldRow>
        </div>

        {/* Sale Proceeds (Cong, letter 104 response 3 and 1.1.3: "Sales" -> "Sale", no tooltip): the last row of Flip Summary, collapsible/expandable (spec 1-4, canvas-
            text.27.08.md:203, "Relocate 'Sales Proceeds' to bottom of this section as a
            collapsible/expandable subsection"). Header value stays visible in both states and
            always equals the panel's own Estimated Sale Proceeds total (spec 5, D3) -- both read
            `computed?.estSaleProceeds`. No tooltip: the info icon is drawn in every frame but no
            copy exists anywhere in the spec for it (D5, FlipSummarySection.jsx's own standing rule
            below: no copy, no icon). */}
        <div className="mt-[8px]">
          <FieldRow
            label="Sale Proceeds"
            control={
              <DisclosureToggle
                collapsedLabel="View details"
                expandedLabel="Hide details"
                expanded={salesProceedsExpanded}
                onToggle={() => setSalesProceedsExpanded((v) => !v)}
              />
            }
          >
            <DerivedValueBox value={formatMoney(computed?.estSaleProceeds)} ariaLabel="Sale Proceeds" />
          </FieldRow>
          {salesProceedsExpanded ? (
            <SalesProceedsPanel
              scenarioType={s.scenarioType}
              scenario={s}
              computed={computed}
              onFieldChange={onFieldChange}
            />
          ) : null}
        </div>
      </div>
    </SectionCard>
  )
}
